
import { useState, useEffect, useCallback } from 'react';
import { AiMarketAnalysisService, AiAnalysisSession } from '@/services/AiMarketAnalysisService';
import { useToast } from '@/hooks/use-toast';

interface UseAiMarketAnalysisProps {
  userId: string | null;
}

interface UseAiMarketAnalysisReturn {
  startAnalysis: (symbol: string, marketSummaryId: string, language?: string) => Promise<void>;
  analysisSession: AiAnalysisSession | null;
  isAnalyzing: boolean;
  error: string | null;
  stopPolling: () => void;
}

export const useAiMarketAnalysis = ({ userId }: UseAiMarketAnalysisProps): UseAiMarketAnalysisReturn => {
  const [analysisSession, setAnalysisSession] = useState<AiAnalysisSession | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stopPollingRef, setStopPollingRef] = useState<(() => void) | null>(null);
  const { toast } = useToast();

  const startAnalysis = useCallback(async (symbol: string, marketSummaryId: string, language: string = 'vi') => {
    if (!userId) {
      toast({
        title: "Authentication Required",
        description: "Please log in to start AI analysis",
        variant: "destructive"
      });
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    
    try {
      const response = await AiMarketAnalysisService.startAnalysis({
        symbol,
        market_summary_id: marketSummaryId,
        user_id: userId,
        language
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to start analysis');
      }

      if (response.session_id) {
        // Start more aggressive polling for real-time updates (1.5 seconds instead of 2)
        const stopPolling = await AiMarketAnalysisService.pollAnalysisProgress(
          response.session_id,
          (session) => {
            setAnalysisSession(session);
            console.log(`Analysis progress: Step ${session.current_step}/${session.total_steps} (${session.progress_percentage}%)`);
            
            // Show partial completion toasts
            if (session.current_step > 0 && session.card_results) {
              const completedSteps = Object.values(session.card_results).filter(
                result => result?.status === 'completed'
              ).length;
              
              if (completedSteps > 0) {
                console.log(`${completedSteps} analysis steps completed and available`);
              }
            }
          },
          (session) => {
            setAnalysisSession(session);
            setIsAnalyzing(false);
            toast({
              title: "Analysis Complete",
              description: `AI analysis for ${symbol} has been completed successfully with all 5 steps`,
            });
          },
          (error) => {
            setError(error);
            setIsAnalyzing(false);
            toast({
              title: "Analysis Failed",
              description: error,
              variant: "destructive"
            });
          },
          1500 // More frequent polling (1.5 seconds)
        );
        
        setStopPollingRef(() => stopPolling);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      setIsAnalyzing(false);
      toast({
        title: "Analysis Failed",
        description: errorMessage,
        variant: "destructive"
      });
    }
  }, [userId, toast]);

  const stopPolling = useCallback(() => {
    if (stopPollingRef) {
      stopPollingRef();
      setStopPollingRef(null);
      setIsAnalyzing(false);
    }
  }, [stopPollingRef]);

  useEffect(() => {
    return () => {
      if (stopPollingRef) {
        stopPollingRef();
      }
    };
  }, [stopPollingRef]);

  return {
    startAnalysis,
    analysisSession,
    isAnalyzing,
    error,
    stopPolling
  };
};
